import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeftRight } from 'lucide-react'

import UserData from '../interfaces/UserData'
import { useAuth } from '../hooks/useAuth'
import Reservation, { Additional, Payment } from '../interfaces/Reservation'
import { formatDateToYYYY_MM_DD, formatWithThousandSep, MessageCrea } from '../utils/utils'
import Vehicle from '../interfaces/Vehicle'
import { FormatProps, handleInputChangeFormat } from '../utils/handleInputChangeFormat'
import Client from '../interfaces/Client'
import { AppPath } from '../enums/AppPath'
import useVehicleFetcher from '../hooks/useVehicleFetcher'
import { NvvAction } from '../enums/DocActions'
import useSalenoteCrea from '../hooks/useSalenoteCrea'
import useSalenoteDiscountApprove from '../hooks/useSalenoteDiscountApprove'
import { PaymentEx, SalenoteDto } from '../interfaces/SalenoteDto'
import { Salenote, SaleNotesCredit, SaleNotesTradeIn } from '../interfaces/Salenote'
import useCreditFetcher from '../hooks/useCreditFetcher'
import Credit from '../interfaces/Credit'
import { Rule, validateFields } from '../utils/validateForm'
import {
  generatePayVehicle,
  tradeInToPaycost,
  transformToTradeIn,
  vehicleMakeDescription,
} from '../utils/reservationNvvShared'
import Deal from '../interfaces/Deal'
import { UserProfileRole } from '../modules/users/types/UserProfile'
import { TransferInitDialog } from '../modules/transfers/components/TransferInitDialog'

import { TotalDisplay } from './SalenoteDetail'
import { PayVehicle } from './ReservationNvvEdit'
import SalenoteCredit from './SalenoteCredit'
import SalenoteDiscountComponent from './SalenoteDiscount'
import SalenotePreview from './SalenotePreview'
import DocAnula from './DocAnula'
import SalenoteReassign from './SalenoteReassign'
import DocConfirma from './DocConfirma'
import VehicleEdit from './VehicleEdit'
import { ClientCard } from './ClientCard'
import VehicleCard from './VehicleCard'
import { Info } from './MessageInfo'
import { ButtonAction, ButtonMenuSet, ButtonPrint } from './Buttons'
import ReservationVehiculo from './ReservationVehiculo'
import ReservationAdicional from './ReservationAdicional'
import ReservationPago from './ReservationPago'
import InputField from './InputField'
import InputTextArea from './InputTextArea'
import DisplayError from './DisplayError'
import { DisplayValueVert } from './DisplayValue'

import type { SalenoteDiscount } from '../interfaces/Salenote'

export interface SalenoteFormat extends SalenoteDto {
  price_format: string
  balance_format: string
  vehicles: PayVehicle[]
  profile_name?: string
  user_phone?: string
  discount?: SalenoteDiscount
  compraPara?: {
    enabled: boolean
    ownerName: string
    ownerRut: string
  }
}

/*
                              salenote   reservation
                              -----------------
Crea NVV a partir de Reserva: undefined   con_datos
Crea NVV desde el botón       undefined   undefined
Modifica NVV                  con_datos   undefined
*/

const SalenoteEdit: React.FC<{
  vehicle: Vehicle
  client: Client
  salenote?: Salenote
  reservation?: Reservation
  handleAnularNvv?: (motivo: string) => void
  handleAprobarNvv?: () => void
  handleReassigned?: () => void
  onRefresh?: () => void
  onCreate?: (id: string) => void
  deal?: Deal
  loadingApprove?: boolean
  loadingVoid?: boolean
}> = ({
  vehicle,
  client,
  salenote,
  reservation,
  handleAnularNvv,
  handleAprobarNvv,
  handleReassigned,
  onRefresh,
  onCreate,
  deal,
  loadingApprove,
  loadingVoid,
}) => {
  const { userData, hasRole } = useAuth()
  const [formData, setFormData] = useState<SalenoteFormat | null>(null)
  const appraisal = vehicle.appraisal ?? 0
  const [showPreview, setShowPreview] = useState<boolean>(false)
  const [showTransferDialog, setShowTransferDialog] = useState<boolean>(false)
  const [alertMessage, setAlertMessage] = useState('')
  const [infoMessage, setInfoMessage] = useState('')
  const { errorSalenoteCrea, loading: loadingCrea, salenoteCrea } = useSalenoteCrea()
  const { errorSalenoteDiscountApprove, salenoteDiscountApprove } = useSalenoteDiscountApprove()
  const [modificandoSw, setModificandoSw] = useState<boolean>(false)
  const [vehicleShow, setVehicleShow] = useState<boolean>(false)
  //BEGIN lo siguiente es parte del estado de ReservationVehiculo
  // que mantenemos aca para poder llamar a la que crea vehoculo
  const [vehShowVehicleEdit, setVehShowVehicleEdit] = useState<boolean>(false)
  const [vehNewPayment, setVehNewPayment] = useState<PayVehicle | null>(generatePayVehicle)
  // Errores de validacion de los input
  const [vehErrors, setVehErrors] = useState<{ [key: string]: string }>({})
  const [vehVehicle, setVehVehicle] = useState<Vehicle | null>(null)
  const { errorVehicle, fetchVehicle } = useVehicleFetcher()
  const [totalCost, setTotalCost] = useState<number>(0)
  const [totalPayments, setTotalPayments] = useState<number>(0)
  const [credits, setCredits] = useState<Credit[]>([])
  // POR AHOTA, no chequeamos error en el siguiente
  const { fetchCreditsByVehicleClient } = useCreditFetcher()
  const [errorsClickCrear, setErrorsClickCrear] = useState<{ [key: string]: string }>({})

  //END----------------------------------

  const salenotesTradeInToPaycost = (trades: SaleNotesTradeIn[]): PayVehicle[] => {
    return trades.map((trade: SaleNotesTradeIn) => ({
      key: trade._id,
      price: trade.price, // mapping 'price' to 'cost'
      description: vehicleMakeDescription(trade.vehicle), // same field names
      date: '', // same field names
      price_format: formatWithThousandSep(trade.price),
      vehicle: trade.vehicle,

      // NEW: Map purchase tracking fields
      associatedPurchaseId: trade.associatedPurchaseId,
      purchaseCreated: trade.purchaseCreated,
      // purchaseNumber will be fetched separately if needed
    }))
  }

  // Aca colocamos los valores iniciales.
  // Aca es donde hay que revisar cada vez que se incorpora una nueva propiedad
  // a "salenote"
  useEffect(() => {
    if (salenote) {
      const payments = salenote.payments.map((item) => ({ ...item, fromReservation: false }))
      // Veamos si hay payments de la reserva
      const rv = salenote.body.reservation
      const hayRv = salenote.reservation && rv
      const paymentsRv = hayRv
        ? payments.concat((rv.payments ?? []).map((item) => ({ ...item, fromReservation: true })))
        : payments

      // El credit que viene en la salenote es simple. El
      // que se maneja en "Dto" tiene mas datos
      const mycredit = salenotesCreditToCredit(salenote.credit)

      setFormData({
        ...salenoteInitial(userData, vehicle, client, salenote, null),
        payments: paymentsRv,
        additionals: salenote.additionals ?? [],
        vehicles: salenotesTradeInToPaycost(salenote.tradeIns ?? []),
        credit: mycredit,
      })

      // Set discount state for existing sale notes
      if (salenote.discount) {
      }

      vistaPreviaClick()
    } else if (reservation) {
      const aux = tradeInToPaycost(reservation.tradeIns ?? [], reservation.tradeInVehicles ?? [])
      setFormData({
        ...salenoteInitial(userData, vehicle, client, null, reservation),
        payments: (reservation.payments ?? []).map((item) => ({ ...item, fromReservation: true })),
        additionals: reservation.additionals ?? [],
        vehicles: aux,
      })

      const allCost = totalCostCalc(reservation.additionals ?? [], reservation.price)
      setTotalCost(allCost)
    } else {
      setTotalCost(vehicle.price)
      setFormData(salenoteInitial(userData, vehicle, client, null, null, deal))
    }
  }, [])

  const totalCostCalc = (costArray: Additional[], price: number) => {
    const suma = costArray.reduce((sum, current) => sum + Number(current?.cost ?? 0), 0)

    return suma + Number(price)
  }
  // Effect to update alertMessage whenever errorReservationCrea changes

  useEffect(() => {
    if (errorSalenoteCrea) {
      setAlertMessage(errorSalenoteCrea.message)
    }
  }, [errorSalenoteCrea])

  // Cuando se escribe algo en el "Precio Venta" o la fecha
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name } = e.target

    const formatProps: FormatProps[] = [{ name: 'price', name_format: 'price_format', isDecimal: false }]
    handleInputChangeFormat<SalenoteFormat>(e, formData, setFormData, formatProps)

    if (name !== 'price_format') return

    if (formData) {
      let numericValue = e.target.value.replace(/[^\d]/g, '')

      if (numericValue == '') numericValue = '0'

      const allCost = totalCostCalc(formData.additionals ?? [], Number(numericValue))
      setTotalCost(allCost)

      // Discount calculation logic
      const discountAmount = vehicle.price - Number(numericValue)
      const hasDiscountNow = discountAmount > 0

      setFormData((prevFormData) => {
        if (!prevFormData) return null // To satisfy TypeScript null-checks
        const balance = calcAmount(null, null, null, null, prevFormData?.price)

        return {
          ...prevFormData,
          balance: balance,
          balance_format: formatWithThousandSep(balance),
          discount: hasDiscountNow
            ? {
                originalPrice: vehicle.price,
                discountedPrice: Number(numericValue),
                discountAmount: discountAmount,
                requiresApproval: true,
              }
            : undefined,
        }
      })
    }
  }

  const calcAmount = (
    updatedCosts: Additional[] | null,
    updatedPayments: Payment[] | null,
    updatedVehicles: PayVehicle[] | null,
    updatedCredit: Credit | null,
    price: number
  ): number => {
    const sumTot = sumAddPagosVeh(updatedCosts, updatedPayments, updatedVehicles, updatedCredit)

    return -1 * (Number(sumTot) + Number(price))
  }

  /*
    llega cuando huno un cambio en los Vehiculos
    */
  const handleVehiclesChange = (updatedVehicles: PayVehicle[]) => {
    if (formData !== null) {
      const suma = updatedVehicles.reduce((sum, current) => sum + Number(current?.price ?? 0), 0)
      const suma2 = (formData.payments ?? []).reduce((sum, current) => sum + Number(current?.value ?? 0), 0)
      const suma3 = formData.credit?.amount ?? 0
      setTotalPayments(Number(suma) + Number(suma2) + Number(suma3))

      setFormData((prevFormData) => {
        if (!prevFormData) return null // To satisfy TypeScript null-checks
        const calc = calcAmount(null, null, updatedVehicles, null, prevFormData?.price)

        return {
          ...prevFormData,
          balance: calc,
          balance_format: formatWithThousandSep(calc),
          vehicles: updatedVehicles,
        }
      })
    }
  }

  const handlePaymentsChange = (updatedPayments: PaymentEx[]) => {
    if (formData !== null) {
      // Le sumamos al saldo
      const suma = updatedPayments.reduce((sum, current) => sum + Number(current?.value ?? 0), 0)
      const suma2 = formData.vehicles.reduce((sum, current) => sum + Number(current?.price ?? 0), 0)

      const suma3 = formData.credit?.amount ?? 0
      setTotalPayments(Number(suma) + Number(suma2) + Number(suma3))
      setFormData((prevFormData) => {
        if (!prevFormData) return null // To satisfy TypeScript null-checks

        return {
          ...prevFormData,
          balance_format: formatWithThousandSep(calcAmount(null, updatedPayments, null, null, prevFormData?.price)),
          payments: updatedPayments,
        }
      })
    }
  }

  // Aca cuando selecciona el credito a usar. Recibe null cuando se elimina
  const handleCreditsChange = (updatedCredit: Credit | null) => {
    // Le sumamos al saldo
    if (formData) {
      const suma = updatedCredit?.amount ?? 0
      const suma2 = formData.vehicles.reduce((sum, current) => sum + Number(current?.price ?? 0), 0)
      const suma3 = (formData.payments ?? []).reduce((sum, current) => sum + Number(current?.value ?? 0), 0)
      // Lo iguiente es para dejar "myCredit" undefined cuando borramos
      let myCredit: Credit

      if (updatedCredit) {
        myCredit = updatedCredit
      }
      setTotalPayments(Number(suma) + Number(suma2) + Number(suma3))
      setFormData((prevFormData) => {
        if (!prevFormData) return null // To satisfy TypeScript null-checks

        return {
          ...prevFormData,
          balance_format: formatWithThousandSep(calcAmount(null, null, null, updatedCredit, prevFormData?.price)),
          credit: myCredit,
        }
      })
    }
  }
  /*
    llega cuando hubo un cambio en los Adicionales
    */
  const handleCostsChange = (updatedCosts: Additional[]) => {
    if (formData !== null) {
      const suma = updatedCosts.reduce((sum, current) => sum + Number(current?.cost ?? 0), 0)
      const numericValue = Number(formData.price)

      setTotalCost(Number(suma) + numericValue)

      setFormData((prevFormData) => {
        if (!prevFormData) return null // To satisfy TypeScript null-checks

        return {
          ...prevFormData,
          balance: calcAmount(updatedCosts, null, null, null, prevFormData?.price),
          balance_format: formatWithThousandSep(calcAmount(updatedCosts, null, null, null, prevFormData?.price)),
          additionals: updatedCosts,
        }
      })
    }
  }

  const vistaPreviaClick = () => {
    setShowPreview(true)
  }

  // Aca es cuando se presiona el "Crear en la Preview"
  const navigate = useNavigate()
  const handleCrearOModificar = async () => {
    if (!formData) return

    const creditAmount = formData.credit?.amount ?? 0

    // Tenemos que validar que el saldo sea cero
    const price = formData.price
    const porReservar = (formData.additionals ?? []).reduce(
      (accumulator: number, item: { cost: number }) => Number(accumulator) + item.cost,
      price
    )
    const reservado =
      (formData.payments ?? []).reduce(
        (accumulator: number, item: { value: number }) => Number(accumulator) + item.value,
        0
      ) +
      formData.vehicles.reduce((accumulator: number, item: { price: number }) => Number(accumulator) + item.price, 0) +
      creditAmount
    const saldo = porReservar - reservado
    const fieldsToValidate = [{ name: 'saldo', value: saldo, rule: Rule.LOWER_EQUAL_ZERO }]
    const validationErrors = validateFields(fieldsToValidate)

    if (Object.keys(validationErrors).length > 0) {
      setAlertMessage('El saldo debe ser cero. Por favor, corrije.')

      return
    }
    setErrorsClickCrear({})

    salenoteFill()
    const accion = creandoSw ? 0 : 1

    // Devuelve el ID cuyando estamos creando,
    // nada importante si estamos modificando
    // Si devuelve vacio, nos fue mal.

    const res = await salenoteCrea(formData, accion)

    // refrescamos la pantalla
    if (res === null) {
      return
    }

    const id = creandoSw ? res : formData._id

    if (onRefresh) {
      // Refrescamos, si fuimos llamados desde "Detail"
      // es decir, estamos modificando
      onRefresh()
    } else if (onCreate) {
      onCreate(id)
    } else {
      //volvemos a la ventana de detalle.

      const url = `/${AppPath.SaleNotes}/view/${id}`
      navigate(url)
    }

    const accionMsg = creandoSw ? NvvAction.Crear : NvvAction.Modificar
    const msg = MessageCrea(AppPath.SaleNotes, accionMsg)

    setInfoMessage(msg)
    setModificandoSw(false)
  }

  const salenoteFill = () => {
    if (!formData) return null

    formData.tradeIns = transformToTradeIn(formData.vehicles)
    // Los demas  campos  estan en la inicializacion o
    // se mantienen en el formulario
  }
  /*
        const onAlertTimeout = (): void => {
          setAlertMessage("");
        }
          */
  const onInfoTimeout = (): void => {
    setInfoMessage('')
  }
  // The callback to go back to the edit view
  const handleGoBack = () => {
    setAlertMessage('')
    setShowPreview(false)
  }
  // The callback to go back to the edit view
  const handleGoBackEdit = () => {
    setModificandoSw(true)
    setShowPreview(false)
  }

  const sumAddPagosVeh = (
    updatedCosts: Additional[] | null,
    updatedPayments: Payment[] | null,
    updatedVehicles: PayVehicle[] | null,
    updatedCredit: Credit | null
  ) => {
    // Primero, sumemos todos los costos
    if (!formData) return
    const costArray = updatedCosts ?? formData.additionals ?? []
    const suma = costArray.reduce((sum, current) => sum + Number(current?.cost ?? 0), 0)

    const payArray = updatedPayments ?? formData.payments ?? []
    const resta = payArray.reduce((sum, current) => sum + Number(current?.value ?? 0), 0)

    const vehArray = updatedVehicles ?? formData.vehicles
    const restaVeh = vehArray.reduce((sum, current) => sum + Number(current?.price ?? 0), 0)

    const cred = updatedCredit ?? formData.credit
    const restaCre = cred ? cred.amount : 0

    return Number(suma) - Number(resta) - Number(restaVeh) - Number(restaCre)
  }

  const onCancelModify = () => {
    setModificandoSw(false)
  }
  // Si noviene el eid
  const creandoSw = !salenote

  // Aca llegamos cuando hay click en Crear en la grilla de vehiculos
  // Debemos invocar a <VehicleEdit/>

  const onVehicleCreate = () => {
    setVehicleShow(true)
  }

  // Aca cuando volvemos de crear el vehiculo
  const onVehicleCreateReturn = async (id: string) => {
    setVehicleShow(false)

    // Leemos el vehiculo
    if (!id || id === '') {
      return
    }

    const veh = await fetchVehicle(id)

    if (!errorVehicle) {
      setVehVehicle(veh)
      setVehShowVehicleEdit(true)
    } else {
      setVehVehicle(null)
      // setWarningMessage("No existe vehículo con esa patente para usar como parte de pago.")
    }
  }
  const esAnulada = !!salenote?.voidedAt
  const esAprobada = !!salenote?.approvedAt

  /** Veamos i hay algun credito. Pueden haber mas de uno */
  useEffect(() => {
    const fetchDataAsync = async () => {
      // Busquemos los creditos del vehiculo - cliente
      const credits = await fetchCreditsByVehicleClient(vehicle._id, client._id)
      const approvedCredits = credits.filter((c) => c.status === 'STATUS.CREDIT.APPLICATION.APPROVED')
      setCredits(approvedCredits)
    }
    fetchDataAsync()
  }, [salenote, vehicle, client])

  const verContratoClick = () => {
    window.open(`/fullscreen/${AppPath.SaleNotes}/contract?id=${salenote?._id}`, '_blank')
  }
  const verResponsabilityClick = () => {
    window.open(`/fullscreen/${AppPath.SaleNotes}/responsibility?id=${salenote?._id}`, '_blank')
  }

  // Handle discount approval during creation (pre-approval)
  const handlePreApproveDiscount = () => {
    if (!userData || !formData?.discount) return

    // Update formData to mark discount as approved
    setFormData((prevFormData) => {
      if (!prevFormData || !prevFormData.discount) return prevFormData

      return {
        ...prevFormData,
        discount: {
          ...prevFormData.discount,
          discountApprovedAt: new Date().toISOString(),
          discountApprovedBy: userData.profile?._id ?? '',
        },
      }
    })

    setInfoMessage('Descuento pre-aprobado. Se guardará al crear la nota de venta.')
  }

  // Handle discount approval for existing sale note
  const handleApproveDiscount = async () => {
    if (!salenote || !userData) return

    const id = salenote._id
    const by = userData.profile?._id ?? ''
    const res = await salenoteDiscountApprove(id, by)

    if (res === null) {
      setAlertMessage(errorSalenoteDiscountApprove?.message || 'Error al aprobar descuento')

      return
    }

    const msg = 'Descuento aprobado exitosamente'
    setInfoMessage(msg)

    // Update local formData state to reflect the approval
    setFormData((prevFormData) => {
      if (!prevFormData || !prevFormData.discount) return prevFormData

      return {
        ...prevFormData,
        discount: {
          ...prevFormData.discount,
          discountApprovedAt: new Date().toISOString(),
          discountApprovedBy: by,
        },
      }
    })

    // Refresh the view if onRefresh is provided
    if (onRefresh) {
      onRefresh()
    }
  }

  // Calculate approval permission - now only needs ROLE.SALE_NOTES_APPROVE
  const hasApprovePermission = hasRole(UserProfileRole.ApproveSaleNotes)

  return (
    <div className="mb-8">
      {vehicleShow ? (
        <VehicleEdit mode={false} onReturn={onVehicleCreateReturn} />
      ) : (
        <div className="p-4">
          {!showPreview && (creandoSw || modificandoSw) ? (
            <div>
              {!creandoSw && (
                <div className="flex flex-col items-center mb-4">
                  <div className="flex flex-wrap gap-4 justify-center items-start">
                    <VehicleCard vehicle={vehicle} />
                    <ClientCard client={client} />
                  </div>
                </div>
              )}
              {creandoSw && (
                <div>
                  <div className="text-2xl border flex items-center justify-center">Creando nota de venta</div>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                {creandoSw ? (
                  <div>
                    <InputField
                      label="Número"
                      name="number"
                      value={formData?.number}
                      type="number"
                      handleInputChange={handleInputChange}
                    />
                    <div className="text-sm">Deja en cero para numeración automática</div>
                  </div>
                ) : (
                  <DisplayValueVert label="Número" name="number" value={formData?.number} />
                )}
                <InputField
                  label="Precio Venta"
                  name="price_format"
                  value={formData?.price_format}
                  type="string"
                  handleInputChange={handleInputChange}
                />
                <InputField
                  label="Fecha"
                  name="date"
                  value={formData?.date}
                  type="date"
                  handleInputChange={handleInputChange}
                />

                <div className="text-right">
                  <DisplayValueVertRes label="Saldo" value={formData?.balance_format} />
                </div>

                {formData?.discount && !formData?.discount?.discountApprovedAt && hasApprovePermission && (
                  <div className="mt-2 flex justify-end col-span-full">
                    <button
                      onClick={creandoSw ? handlePreApproveDiscount : handleApproveDiscount}
                      className="px-4 py-2 text-sm font-medium text-white bg-pink-500 hover:bg-pink-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 transition-colors"
                    >
                      Aprobar descuento
                    </button>
                  </div>
                )}
                {formData?.discount?.discountApprovedAt && (
                  <div className="mt-2 col-span-full">
                    <div className="bg-green-50 border border-green-200 rounded-md p-3">
                      <div className="flex items-center">
                        <svg
                          className="w-5 h-5 text-green-600 mr-2 flex-shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <div className="text-sm text-green-700 font-medium">Descuento aprobado</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Compra Para section placed horizontally outside the main grid */}
              <div className="border border-gray-400 p-4 mt-4 rounded-md">
                <div className="flex items-center justify-between border-b border-gray-300 pb-2 mb-2">
                  <h5 className="text-xl font-bold uppercase text-gray-700">COMPRA PARA</h5>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <span className="text-sm font-medium text-gray-700">Habilitar compra para tercero</span>
                    <input
                      type="checkbox"
                      checked={formData?.compraPara?.enabled ?? false}
                      onChange={(e) => {
                        handleInputChange({
                          target: {
                            name: 'compraPara',
                            value: {
                              ...(formData?.compraPara || { ownerName: '', ownerRut: '' }),
                              enabled: e.target.checked,
                            },
                          },
                        } as any)
                      }}
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                  </label>
                </div>
                {formData?.compraPara?.enabled && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InputField
                      label="Nombre"
                      name="compraPara.ownerName"
                      value={formData?.compraPara?.ownerName ?? ''}
                      type="string"
                      handleInputChange={(e: any) => {
                        handleInputChange({
                          target: {
                            name: 'compraPara',
                            value: {
                              ...(formData?.compraPara || { enabled: true, ownerRut: '' }),
                              ownerName: e.target.value,
                            },
                          },
                        } as any)
                      }}
                    />
                    <InputField
                      label="RUT"
                      name="compraPara.ownerRut"
                      value={formData?.compraPara?.ownerRut ?? ''}
                      type="string"
                      handleInputChange={(e: any) => {
                        handleInputChange({
                          target: {
                            name: 'compraPara',
                            value: {
                              ...(formData?.compraPara || { enabled: true, ownerName: '' }),
                              ownerRut: e.target.value,
                            },
                          },
                        } as any)
                      }}
                    />
                  </div>
                )}
              </div>

              <div className="border  shadow-lg rounded-md p-2 mt-2">
                <ReservationAdicional
                  title="Adicionales"
                  vehiclePrice={formData?.price ?? 0}
                  appraisal={appraisal}
                  array={formData?.additionals ?? []}
                  onPaymentsChange={handleCostsChange}
                />
              </div>
              {formData?.discount && (
                <div className="border shadow-lg rounded-md p-2 mt-2">
                  <SalenoteDiscountComponent title="Descuentos" discount={formData?.discount!} />
                </div>
              )}
              <TotalDisplay title="Total a pagar" total={totalCost} />

              <div className="border shadow-lg rounded-md p-2 mt-2">
                <ReservationPago
                  title="Pagos"
                  array={formData?.payments ?? []}
                  onPaymentsChange={handlePaymentsChange}
                />
              </div>
              <div className="border shadow-lg rounded-md p-2 mt-2">
                <SalenoteCredit
                  title="Créditos"
                  array={credits}
                  credit={formData?.credit ?? null}
                  onCreditChange={handleCreditsChange}
                />
              </div>
              <div className="border shadow-lg rounded-md p-2 mt-2">
                <ReservationVehiculo
                  title="Vehículos"
                  array={formData?.vehicles ?? []}
                  patenteReservada={vehicle.plate}
                  onVehicleCreate={onVehicleCreate}
                  onPaymentsChange={handleVehiclesChange}
                  vehicleMakeDescription={vehicleMakeDescription}
                  vehShowVehicleEdit={vehShowVehicleEdit}
                  setVehShowVehicleEdit={setVehShowVehicleEdit}
                  vehNewPayment={vehNewPayment}
                  setVehNewPayment={setVehNewPayment}
                  vehErrors={vehErrors}
                  setVehErrors={setVehErrors}
                  vehVehicle={vehVehicle}
                  setVehVehicle={setVehVehicle}
                />
              </div>
              <TotalDisplay title="Total pagos" total={totalPayments} />
              <div>
                <InputField
                  type="string"
                  label="Compañía de seguros (opcional)"
                  name="insuranceCo"
                  value={formData?.insuranceCo}
                  handleInputChange={handleInputChange}
                />
              </div>
              <InputTextArea
                label="Observaciones"
                name="notes"
                value={formData?.notes}
                handleInputChange={handleInputChange}
              />
            </div>
          ) : (
            <>
              {formData && (
                <SalenotePreview
                  vehicle={vehicle}
                  client={client}
                  salenote={formData}
                  creandoSw={creandoSw}
                  modificandoSw={modificandoSw}
                  errors={errorsClickCrear}
                  onGoBack={handleGoBack}
                  onCrearClick={handleCrearOModificar}
                  onCancelModify={onCancelModify}
                  //por ahora
                  esAprobada={esAprobada}
                  esAnulada={esAnulada}
                  loading={loadingCrea}
                />
              )}
              {!creandoSw && !modificandoSw && formData && (
                <>
                  <div className="mb-4">
                    <ButtonPrint />
                    {esAprobada && !esAnulada && (
                      <div className="flex justify-center items-center m-2 print:hidden">
                        <div className="mr-2">
                          <ButtonAction label="Contrato de Compraventa" handleButtonClick={verContratoClick} />
                        </div>
                        <ButtonAction
                          label="Declaración de Responsabilidad"
                          handleButtonClick={verResponsabilityClick}
                        />
                      </div>
                    )}
                  </div>
                  {!esAnulada &&
                    !esAprobada &&
                    salenote?.discount?.requiresApproval &&
                    !salenote?.discount?.discountApprovedAt && (
                      <div className="print:hidden mb-4 max-w-3xl mx-auto">
                        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 shadow-md">
                          <div className="flex items-start">
                            <div className="flex-shrink-0">
                              <svg
                                className="h-6 w-6 text-yellow-400"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                                />
                              </svg>
                            </div>
                            <div className="ml-3 flex-1">
                              <h3 className="text-sm font-medium text-yellow-800">Descuento pendiente de aprobación</h3>
                              <div className="mt-2 text-sm text-yellow-700">
                                <p>
                                  Esta nota de venta tiene un descuento aplicado que aún no ha sido aprobado. Para
                                  aprobar el descuento, haga clic en el botón <strong>"Aprobar descuento"</strong> a
                                  continuación. Una vez aprobado el descuento, podrá aprobar la nota de venta.
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  {!esAnulada && (
                    <div className="print:hidden">
                      <SubMenuNvv
                        handleGoBackEdit={handleGoBackEdit}
                        handleAprobar={handleAprobarNvv}
                        handleAnular={handleAnularNvv}
                        handleApproveDiscount={handleApproveDiscount}
                        handleReassigned={handleReassigned}
                        salenote={salenote}
                        esAprobada={esAprobada}
                        hasApprovePermission={hasApprovePermission}
                        hasUnapprovedDiscount={
                          !!(
                            !esAprobada &&
                            salenote?.discount?.requiresApproval &&
                            !salenote?.discount?.discountApprovedAt
                          )
                        }
                        loadingApprove={loadingApprove}
                        loadingVoid={loadingVoid}
                      />
                      {/* Transfer button - shown for approved salenotes when user can edit transfers or sale notes */}
                      {esAprobada &&
                        ['5a628323cdb9dd31b15b85a8', '66b51a545e7ba00ea85cc5b6'].includes(
                          userData?.organization?._id || ''
                        ) &&
                        (hasRole(UserProfileRole.EditTransfers) || hasRole(UserProfileRole.EditSaleNotes)) &&
                        salenote && (
                          <div className="mt-3 flex justify-center">
                            <button
                              onClick={() => setShowTransferDialog(true)}
                              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 transition-colors"
                            >
                              <ArrowLeftRight className="w-4 h-4" />
                              Enviar a Transferencia
                            </button>
                            <TransferInitDialog
                              open={showTransferDialog}
                              onClose={() => setShowTransferDialog(false)}
                              salenote={salenote}
                              onSuccess={(transferId) => {
                                setShowTransferDialog(false)
                                navigate(`/${AppPath.Transfers}/view/${transferId}`)
                              }}
                            />
                          </div>
                        )}
                    </div>
                  )}
                </>
              )}
            </>
          )}
          {!showPreview && (creandoSw || modificandoSw) && (
            <div className="flex justify-center mt-4">
              <ButtonAction label="Vista Previa" handleButtonClick={vistaPreviaClick} />
            </div>
          )}
          <div className="mb-10">
            <DisplayError msg={alertMessage} />
          </div>
          <Info message={infoMessage} trigger={Date.now()} onTimeout={onInfoTimeout} />
        </div>
      )}
    </div>
  )
}

export default SalenoteEdit

// Despliega el Label sobre el valor. Coloca borde rojo
// si el primer caracter es "-".
export const DisplayValueVertRes: React.FC<{ label: string; value?: string }> = ({ label, value }) => {
  const myClassName = 'flex flex-col'
  const signo = value?.startsWith('-')
  const borderColor = signo ? 'ring-4 ring-red-500' : ''

  return (
    <div className={myClassName}>
      <label className="font-semibold mb-1 text-center">{label}</label>
      <label className={`border rounded p-2 ${borderColor}`}>{value}</label>
    </div>
  )
}

import { useFeatureFlag } from '../hooks/useFeatureFlag'

const SubMenuNvv: React.FC<{
  handleGoBackEdit: () => void
  handleAprobar?: () => void
  handleAnular?: (motivo: string) => void
  handleApproveDiscount?: () => void
  handleReassigned?: () => void
  salenote?: Salenote
  esAprobada: boolean
  hasApprovePermission: boolean
  hasUnapprovedDiscount?: boolean
  loadingApprove?: boolean
  loadingVoid?: boolean
}> = ({
  handleGoBackEdit,
  handleAprobar,
  handleAnular,
  handleApproveDiscount,
  handleReassigned,
  salenote,
  esAprobada,
  hasApprovePermission,
  hasUnapprovedDiscount = false,
  loadingApprove,
  loadingVoid,
}) => {
  const { useNewDocumentUI } = useFeatureFlag()
  const [clickedIndex, setClickedIndex] = useState<number>(-1)
  const { hasRole } = useAuth()

  const handleMenuClick = (estado: number) => {
    setClickedIndex(estado)
  }
  const handleAnularNvv = async () => {
    handleAnular && handleAnular(motivo)
  }
  const handleAprobarNvv = async () => {
    handleAprobar && handleAprobar()
  }
  const handleApproveDiscountClick = async () => {
    handleApproveDiscount && handleApproveDiscount()
    setClickedIndex(-1) // Reset to hide the confirmation message
  }
  const handleApproveDiscountAndSalenote = async () => {
    // First approve the discount, then approve the sale note
    if (handleApproveDiscount && handleAprobar) {
      await handleApproveDiscount()
      // Small delay to ensure discount approval is processed
      setTimeout(async () => {
        await handleAprobar()
      }, 500)
    }
  }
  const handleMotivoChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { value } = e.target

    setMotivo(value)
  }
  useEffect(() => {
    // Scroll al final
    window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' })
  }, [clickedIndex])

  const modificarCaption = hasRole(UserProfileRole.ModifySaleNotes) ? 'Modificar' : ''
  // Show discount approval button if there's an unapproved discount and user has permission
  const aprobarDescuentoCaption = hasUnapprovedDiscount && hasApprovePermission ? 'Aprobar descuento' : ''
  // Show combined button if there's an unapproved discount and user has permission
  const aprobarAmboCaption = hasUnapprovedDiscount && hasApprovePermission ? 'Aprobar descuento y nota de venta' : ''
  // Hide approve button if there's an unapproved discount or user doesn't have permission
  const aprobarCaption = hasApprovePermission && !hasUnapprovedDiscount ? 'Aprobar' : ''
  const anularCaption = hasRole(UserProfileRole.VoidSaleNotes) ? 'Anular' : ''
  // Va al final: los paneles se eligen por posición (clickedIndex) y no hay que correr los demás
  const reassignCaption = !hasRole(UserProfileRole.ReassignSaleNotes)
    ? ''
    : esAprobada
      ? 'Cambiar vendedor o aprobador'
      : 'Cambiar vendedor'

  const buttonCaptions = !esAprobada
    ? [modificarCaption, aprobarDescuentoCaption, aprobarAmboCaption, aprobarCaption, anularCaption, reassignCaption]
    : ['', '', '', '', anularCaption, reassignCaption]

  const [motivo, setMotivo] = useState<string>('')

  return (
    <div className={`mx-auto shadow-md border ${useNewDocumentUI ? 'p-4 max-w-6xl print:hidden' : 'p-2 max-w-3xl'}`}>
      <ButtonMenuSet captions={buttonCaptions} clickedIndex={clickedIndex} handleButtonClick={handleMenuClick} />
      <div className="mt-4">
        {clickedIndex === 0 && !esAprobada && (
          <>
            <div>
              Podrás modificar los datos de esta nota de venta, excepto el cliente y el vehículo. Si deseas
              modificarlos, debes anular esta nota de venta y crear otra.
            </div>
            <div className="flex justify-center mt-4">
              <ButtonAction label="Ir a modificar" handleButtonClick={handleGoBackEdit} />
            </div>
          </>
        )}
        {clickedIndex === 1 && !esAprobada && hasUnapprovedDiscount && (
          <div>
            <div className="space-y-3">
              <div className="bg-green-50 border border-green-200 rounded-md p-4">
                <div className="flex items-start">
                  <svg
                    className="w-5 h-5 text-green-600 mt-0.5 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <div>
                    <h4 className="text-sm font-medium text-green-800">Aprobar descuento</h4>
                    <p className="mt-1 text-sm text-green-700">
                      ¿Está seguro que desea aprobar el descuento aplicado a esta nota de venta? Una vez aprobado, podrá
                      proceder con la aprobación de la nota de venta.
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex justify-center mt-4">
                <ButtonAction label="Sí, aprobar descuento" handleButtonClick={handleApproveDiscountClick} />
              </div>
            </div>
          </div>
        )}
        {clickedIndex === 2 && !esAprobada && hasUnapprovedDiscount && (
          <div>
            <div className="space-y-3">
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                <div className="flex items-start">
                  <svg
                    className="w-5 h-5 text-blue-600 mt-0.5 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <div>
                    <h4 className="text-sm font-medium text-blue-800">Aprobar descuento y nota de venta</h4>
                    <p className="mt-1 text-sm text-blue-700">
                      ¿Está seguro que desea aprobar el descuento y la nota de venta? Esta acción aprobará primero el
                      descuento aplicado y luego procederá con la aprobación de la nota de venta. Una vez aprobada, la
                      nota de venta será legalmente válida.
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex justify-center mt-4">
                <ButtonAction label="Sí, aprobar ambos" handleButtonClick={handleApproveDiscountAndSalenote} />
              </div>
            </div>
          </div>
        )}
        {clickedIndex === 3 && !esAprobada && !hasUnapprovedDiscount && (
          <div>
            <div>Al aprobar la nota de venta esta será legalmente válida .</div>
            <DocConfirma
              docType={AppPath.SaleNotes}
              handleCrearClick={handleAprobarNvv}
              action={NvvAction.Aprobar}
              loading={loadingApprove}
            />
          </div>
        )}

        {clickedIndex === 4 && (
          <DocAnula
            label="Motivo de anulación"
            header={'Al anular la nota de venta esta ya no será legalmente válida.'}
            name="motivo"
            value={motivo}
            docType={AppPath.SaleNotes}
            handleInputChange={handleMotivoChange}
            handleButtonClick={handleAnularNvv}
            loading={loadingVoid}
          />
        )}

        {clickedIndex === 5 && salenote && (
          <SalenoteReassign
            salenote={salenote}
            esAprobada={esAprobada}
            onDone={() => {
              setClickedIndex(-1)
              handleReassigned?.()
            }}
          />
        )}
      </div>
    </div>
  )
}

// OJO, que puede venir de una cotizacion
const salenoteInitial = (
  userData: UserData | null,
  // , selectedPair: SelectedPair | null
  vehicleObj: Vehicle,
  clientObj: Client,
  salenote?: Salenote | null,
  reservationObj?: Reservation | null,
  deal?: Deal
): SalenoteFormat => {
  const today = new Date()
  const today_format = formatDateToYYYY_MM_DD(today)

  if (!userData?.profile?._id || !userData?.user?.fullname) {
    throw new Error('Invalida session')
  }

  let price,
    date,
    notes,
    client,
    vehicle,
    voidedAt,
    balance,
    total,
    reservation,
    number,
    approvedAt = '',
    insuranceCo = '',
    profile_name = '',
    user_phone = '',
    profile = userData?.profile?._id

  if (salenote) {
    //  amount = salenote.amount
    price = salenote.price
    date = salenote.date
    total = salenote.total
    notes = salenote.notes
    client = salenote.client
    vehicle = salenote.vehicle
    balance = salenote.balance
    voidedAt = salenote.voidedAt
    reservation = salenote.reservation
    number = salenote?.number ?? 0
    approvedAt = salenote?.approvedAt ?? ''
    insuranceCo = salenote?.insuranceCo ?? ''
    profile_name = `${salenote.body.profile.user.firstname} ${salenote.body.profile.user.lastname}`
    user_phone = salenote.body.profile.user.phone ?? ''
    profile = salenote.profile
    
    // We already get this naturally initialized by the hook/destructuring above in `salenote.compraPara`
  } else if (reservationObj) {
    price = reservationObj.price
    date = reservationObj.date
    total = reservationObj.price
    notes = reservationObj.notes
    client = reservationObj.client
    vehicle = reservationObj.vehicle
    profile = reservationObj.profile
    profile_name = reservationObj.profile_name ?? ''
    user_phone = reservationObj.user_phone ?? ''
    //TODO
    balance = 0
    voidedAt = reservationObj.voidedAt
    reservation = reservationObj._id
    number = 0
  } else {
    // amount = selectedPair?.price ?? 0
    price = vehicleObj.price
    date = today_format
    //TODO
    balance = 0
    total = 0
    notes = ''
    //   client = selectedPair?.clientId ?? ''
    client = clientObj._id
    //  vehicle = selectedPair?.vehicleId ?? ''
    vehicle = vehicleObj._id
    number = 0
    voidedAt = ''
    reservation = ''

    if (deal) {
      profile_name = deal.profile_user ?? ''
      profile = deal.profile
    } else {
      profile_name = userData?.user?.fullname
      profile = userData?.profile?._id
    }
  }

  return {
    _id: salenote?._id ?? '',
    number: number,
    //TODO:
    balance: balance,
    balance_format: formatWithThousandSep(balance),
    price: price,
    date: date,
    total: total,
    notes: notes,
    organization: userData?.organization?._id ?? '',
    profile,
    client: client,
    vehicle: vehicle,
    price_format: formatWithThousandSep(price),
    footer: '', //salenote?.footer ?? "",
    reservation: reservation ?? '',
    voidedAt: voidedAt,
    approvedAt: approvedAt,
    vehicles: [],
    insuranceCo: insuranceCo,
    profile_name: profile_name,
    user_phone: user_phone,
    discount: salenote?.discount,
    compraPara: salenote?.compraPara
  }
}

/*
Transforma lo que viene en la entidad Credit. No importa que queden en blanco
lo que no se usa, porque esto es para mostrar los datos ppales.
*/
const salenotesCreditToCredit = (c: SaleNotesCredit): Credit | undefined => {
  if (c)
    return {
      _id: c._id,
      amount: c.amount,
      institution: c.institution,
      updatedAt: c.updatedAt,
      fee: 0,
      observations: [],
      status: '',
      deal: '',
      profile: '',
    }
}
